// THE SIZES BUCKETWS DERIVES FOR AN IMAGE, AND THE `srcset` THAT ASKS FOR THEM.
//
// An uploaded raster does not stay one object. bucketws' minimap handler
// (projects/bucketws/api/handlers/minimap-handler) renders a webp at every
// width on its ladder that is narrower than the original — `xs.webp` … up to
// `max.webp` — and its edge picks one of them for any request carrying
// `?width=<n>`: the smallest derived size at least that wide, or the largest it
// has when none is (cdn/origin-request/code/src/handlers/file.js, `getSize`).
// The request reaches it unchanged from here: pagews' own `/file/*` behaviour
// keys its cache on `width` and forwards it, the edge re-points the origin
// without touching the query string, and the tenant's Worker copies `search`
// across (docs/attaching-a-domain.md).
//
// So a page did not need bucketws to do anything new to serve a phone a
// 400-pixel picture instead of a 4000-pixel one. It needed to ASK, and until
// this module the only thing a paragraph ever asked for was the original.
//
// THE LADDER IS COPIED, NOT IMPORTED, AND THAT IS SAFE
//
// It is bucketws' `common/code/minimap`, and `@common` resolves per project,
// so it cannot be imported from here without moving it into a published
// package — a release plus six lockfiles, for six numbers. Drift is the risk
// of a copy, and the edge's rounding is what bounds it: a width bucketws no
// longer derives is rounded UP to the next one it has, and a width above
// everything it has answers the largest. A stale ladder here therefore costs a
// cache entry per stale step and never a wrong picture. `sizes.test.js` pins
// the copy against the values the handler's own test pins on its side.
import { isMinimapExt } from "@repoflow/common/file-utils";
import { FILE_PREFIX } from "./url";

export const MINIMAP_WIDTHS = [100, 200, 400, 800, 1200, 1600];

// The query parameter bucketws' edge reads, and the one pagews' distribution
// keys on. The name is part of two stacks' cache configuration
// (`QueryStringCacheKeys`), so it is spelled once.
export const WIDTH_PARAM = "width";

// `/file/…/content/<name>.<ext>` in any of its forms — stored, authenticated,
// by-host or by-site — with no query string on it yet and an extension the
// pipeline derives sizes for.
//
// The extension is the gate that matters. bucketws generates nothing for a gif
// or an avif, and its edge falls through to the object the URL named for a
// file with no `metadata.sizes` — so a `?width=` on one of those would be
// harmless, but it would also be a cache entry per width for the same bytes.
// An absolute URL is somebody else's image and is never rewritten.
const SIZED_PATH = /^\/file\/[^?#\s]*\/content\/[^/?#\s]+\.([A-Za-z0-9]+)$/;

export const isSizedFileUrl = (url) => {
  if (typeof url !== "string" || !url.startsWith(FILE_PREFIX)) {
    return false;
  }

  const match = SIZED_PATH.exec(url);

  return Boolean(match) && isMinimapExt(match[1].toLowerCase());
};

export const sizedUrl = (url, width) => `${url}?${WIDTH_PARAM}=${width}`;

// The `srcset` for an image, or null when the URL is not one this applies to.
//
// `sourceWidth` is the original's pixel width when the caller knows it — the
// picker does, off the file row's `metadata` — and the candidates are then the
// ladder steps BELOW it, which is exactly the set the handler rendered
// (`width > typedWidth`), plus the original at its own width. That last one is
// what stops a small picture being served smaller than it is: without it the
// browser would ask for 1600 on a wide screen, the edge would answer with the
// largest derived size, and a 500-pixel logo would come back at 400.
//
// Unknown, the whole ladder is offered and the edge caps it. That is a
// migrated gen-2 op, or a file chosen while its sizes were still generating,
// and it can cost at most one ladder step of sharpness against the original —
// which is still the right trade, because the alternative was the original at
// every width for ever.
//
// A source narrower than the smallest step has nothing derived and gets no
// srcset at all: the original IS the only candidate, and `src` already says so.
//
// CANDIDATES ARE JOINED BY A BARE COMMA, AND THAT IS LOAD-BEARING. The
// composer rebases every stored file path in the assembled body by matching
// what PRECEDES it, and whitespace is deliberately not in that class (see
// `rebaseFilePaths` in ./url for why: a path in running prose would be
// rewritten). The HTML grammar allows a comma with no space between
// candidates, so the second and later ones are anchored on the comma instead,
// and the class admits that character. Put a space back and every candidate
// but the first serves a 400 in the preview and on the by-site form.
export const srcsetOf = (url, sourceWidth) => {
  if (!isSizedFileUrl(url)) {
    return null;
  }

  const known = Number.isFinite(sourceWidth) && sourceWidth > 0;
  const steps = known
    ? MINIMAP_WIDTHS.filter((width) => width < sourceWidth)
    : MINIMAP_WIDTHS;

  if (steps.length === 0) {
    return null;
  }

  const candidates = steps.map((width) => `${sizedUrl(url, width)} ${width}w`);

  if (known) {
    candidates.push(`${url} ${Math.round(sourceWidth)}w`);
  }

  return candidates.join(",");
};

// `{ sourceWidth }` for a file row as the picker hands it over, or nothing.
//
// `metadata.width` is bucketws' measurement, written by the minimap handler
// after the EXIF rotation it renders every size through — so it is the width
// of the picture as displayed, which is the one the ladder was chosen from.
// A row still processing has `{}` there and gets nothing, which the renderer
// reads as "unknown" rather than as zero.
export const sourceWidthOf = (file) => {
  const width = Number(file && file.metadata && file.metadata.width);

  return Number.isFinite(width) && width > 0 ? { sourceWidth: width } : {};
};
